// ChatGPT ads (OpenAI) Measurement Pixel — the browser half of the
// `registration_completed` signup conversion.
//
// The base pixel runs on the marketing site (landing/src/layouts/Layout.astro),
// behind marketing-cookie consent. Once consented it writes `__oppref` (ad
// click) and `__obref` (browser), which the landing page mirrors onto
// `.catchagent.ai` as `_getea_oppref` / `_getea_obref`, so they are readable
// here. Like beehiiv's `_bhc`, their presence is our consent gate: without them
// the visitor either never consented or never touched the marketing site, and
// the authenticated app does not load the pixel.
//
// The backend sends the same event through OpenAI's Conversions API
// (backend/src/app/services/openai_ads_conversions.py). OpenAI keeps the first
// copy it receives per (pixel id, event, event id), so the two must agree on
// `registrationEventId`.

// Also in Layout.astro and backend openai_ads_conversions.py — keep all three in sync.
const OPENAI_PIXEL_ID = 'SCrYuz23qLvJ5uRTaqC9bA'
const OPENAI_PIXEL_SRC = 'https://bzrcdn.openai.com/sdk/oaiq.min.js'
const OPENAI_REGISTRATION_EVENT = 'registration_completed'
const OPENAI_PROD_HOST = 'app.catchagent.ai'
const CONSENT_COOKIES = ['_getea_oppref', '_getea_obref']
const DEDUPE_PREFIX = 'openai_registration_fired:'

type OaiqArgs = readonly unknown[]

interface Oaiq {
  (...args: OaiqArgs): void
  q: OaiqArgs[]
}

type OaiqWindow = Window & { oaiq?: Oaiq }

/** Mirrors `registration_event_id` in the backend. */
export function registrationEventId(userId: string): string {
  return `${OPENAI_REGISTRATION_EVENT}:${userId}`
}

function hasConsentCookie(): boolean {
  return document.cookie
    .split(';')
    .some((entry) =>
      CONSENT_COOKIES.some((name) => entry.trim().startsWith(`${name}=`)),
    )
}

function alreadyTracked(userId: string): boolean {
  try {
    return window.localStorage.getItem(DEDUPE_PREFIX + userId) !== null
  } catch {
    return false
  }
}

function markTracked(userId: string): void {
  try {
    window.localStorage.setItem(DEDUPE_PREFIX + userId, '1')
  } catch {
    // Best-effort; the server copy shares the event id, so OpenAI dedupes.
  }
}

// The official loader stub: a queue the SDK drains once it loads.
function ensureOaiqLoaded(w: OaiqWindow): Oaiq {
  const existing = w.oaiq
  if (existing) return existing

  const stub = ((...args: OaiqArgs): void => {
    stub.q.push(args)
  }) as Oaiq
  stub.q = []
  w.oaiq = stub

  const script = document.createElement('script')
  script.async = true
  script.src = OPENAI_PIXEL_SRC
  const firstScript = document.getElementsByTagName('script')[0]
  if (firstScript?.parentNode) {
    firstScript.parentNode.insertBefore(script, firstScript)
  } else {
    document.head.appendChild(script)
  }
  return stub
}

async function sha256Hex(value: string): Promise<string | null> {
  if (typeof crypto === 'undefined' || !crypto.subtle) return null
  try {
    const digest = await crypto.subtle.digest(
      'SHA-256',
      new TextEncoder().encode(value),
    )
    return Array.from(new Uint8Array(digest))
      .map((byte) => byte.toString(16).padStart(2, '0'))
      .join('')
  } catch {
    return null
  }
}

/**
 * Fire `registration_completed` for a new business-email signup.
 *
 * No-op without the mirrored pixel cookies (no consent / never on the
 * marketing site), and at most once per user per browser. The caller owns the
 * business-email and new-signup gates, matching Meta's `CompleteRegistrationBusiness`.
 */
export async function trackOpenAiRegistration(
  userId: string,
  email: string,
): Promise<void> {
  if (typeof window === 'undefined' || typeof document === 'undefined') return
  if (!hasConsentCookie() || alreadyTracked(userId)) return
  markTracked(userId)

  // OpenAI's normalisation: email trimmed and lowercased; external id as-is.
  const [emailSha256, externalIdSha256] = await Promise.all([
    sha256Hex(email.trim().toLowerCase()),
    sha256Hex(userId),
  ])
  const user = {
    ...(emailSha256 ? { email_sha256: emailSha256 } : {}),
    ...(externalIdSha256 ? { external_id_sha256: externalIdSha256 } : {}),
  }

  const oaiq = ensureOaiqLoaded(window as OaiqWindow)
  oaiq('init', {
    pixelId: OPENAI_PIXEL_ID,
    debug: window.location.hostname !== OPENAI_PROD_HOST,
    user,
  })
  oaiq(
    'measure',
    OPENAI_REGISTRATION_EVENT,
    { type: 'customer_action' },
    { event_id: registrationEventId(userId) },
  )
}
